import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "react-hot-toast";
import { formatPhone, formatCPF, formatCEP, cleanNumbers, isValidCEPLength } from "@/utils/formatters";
import { 
  ArrowLeft, Home, BarChart3, Users, Settings, 
  Bell, Search, ChevronRight, Building, Calendar,
  DollarSign, RefreshCw, CreditCard, Plus, Edit,
  Trash2, Clock, FileText, UserCheck, UserX,
  MapPin, Phone, Mail, IdCard, Briefcase, TrendingUp,
  CheckCircle, AlertTriangle, Loader2, Key
} from "lucide-react";
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { useTableColumns } from '@/hooks/useTableColumns';
import { ResponsiveMetricsGrid } from '@/components/ui/responsive-charts';
import { TouchInput } from '@/components/ui/responsive-form';
import { clientService, Client, CreateClientData, UpdateClientData, ClientStats } from '@/services/clientService';

const AdminClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<ClientStats>({
    total: 0,
    active: 0,
    approved: 0,
    blocked: 0,
    pending: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingCep, setLoadingCep] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [savingResetPassword, setSavingResetPassword] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [resetPasswordClient, setResetPasswordClient] = useState<Client | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    nome_completo: "",
    telefone: "",
    cpf: "",
    profissao: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    is_active: true,
    approved: false
  });

  // Carregar clientes
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientService.listClients({
        search: searchTerm || undefined,
        limit: 50
      });
      setClients(response.data);
    } catch (error: unknown) {
      console.error('Erro ao carregar clientes:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar clientes';
      toast.error('Erro ao carregar clientes: ' + message);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Carregar estatísticas
  const loadStats = useCallback(async () => {
    try {
      const statsData = await clientService.getClientStats();
      setStats(statsData);
    } catch (error: unknown) {
      console.error('Erro ao carregar estatísticas:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar estatísticas';
      toast.error('Erro ao carregar estatísticas: ' + message);
    }
  }, []);

  // Executa apenas no mount: inicializa estatísticas
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Recarregar quando busca mudar
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      loadClients();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [loadClients]);

  // Função para buscar endereço por CEP
  const handleCepChange = async (cep: string) => {
    const formattedCep = formatCEP(cep);
    setFormData({ ...formData, cep: formattedCep });

    // Se CEP está completo (8 dígitos), buscar endereço
    if (isValidCEPLength(formattedCep)) {
      setLoadingCep(true);
      try {
        const cleanCep = cleanNumbers(formattedCep);
        const address = await clientService.getAddressByCep(cleanCep);
        
        setFormData(prev => ({
          ...prev,
          logradouro: address.logradouro,
          bairro: address.bairro,
          cidade: address.cidade,
          estado: address.estado
        }));
        
        toast.success('Endereço encontrado!');
      } catch (error: unknown) {
        console.error('Erro ao buscar CEP:', error);
        const message = error instanceof Error ? error.message : 'CEP não encontrado';
        toast.error('CEP não encontrado: ' + message);
      } finally {
        setLoadingCep(false);
      }
    }
  };

  // Função para formatar telefone
  const handlePhoneChange = (phone: string) => {
    const formattedPhone = formatPhone(phone);
    setFormData({ ...formData, telefone: formattedPhone });
  };

  // Função para formatar CPF
  const handleCpfChange = (cpf: string) => {
    const formattedCpf = formatCPF(cpf);
    setFormData({ ...formData, cpf: formattedCpf });
  };



  // Função para verificar se cliente está bloqueado
  function isClientBlocked(client: Client): boolean {
    if (!client.bloqueio_ate) return false;
    return new Date(client.bloqueio_ate) > new Date();
  }

  const generateRandomPassword = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const randomValues = new Uint32Array(6);
    window.crypto.getRandomValues(randomValues);
    return Array.from(randomValues)
      .map((value) => chars[value % chars.length])
      .join('');
  };

  // Configuração das colunas da tabela
  const { createColumn } = useTableColumns<Client>();
  
  const columns: ColumnDef<Client>[] = [
    createColumn('nome_completo', 'Nome', (client) => (
      <div>
        <p className="font-medium uppercase">{client.nome_completo}</p>
        <p className="text-sm text-gray-500">{client.email}</p>
      </div>
    )),
    createColumn('telefone', 'Telefone', (client) => formatPhone(client.telefone)),
    createColumn('cpf', 'CPF', (client) => formatCPF(client.cpf)),
    createColumn('is_active', 'Status', (client) => (
      <Badge variant={client.is_active ? "default" : "secondary"}>
        {client.is_active ? "Ativo" : "Inativo"}
      </Badge>
    )),
    createColumn('approved', 'Aprovação', (client) => (
      <Badge variant={client.approved ? "default" : "destructive"}>
        {client.approved ? "Aprovado" : "Pendente"}
      </Badge>
    )),
    createColumn('actions', 'Ações', (client) => (
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => handleEditClient(client)}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => handleOpenResetPassword(client)}>
          <Key className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="destructive" onClick={() => handleDeleteClient(client.id)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    ))
  ];

  // Métricas dos clientes usando dados do backend
  const metrics = [
    {
      title: "Total Clientes",
      value: stats.total,
      change: `${stats.active} ativos`,
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: Users
    },
    {
      title: "Clientes Aprovados", 
      value: stats.approved,
      change: `${Math.round((stats.approved / Math.max(stats.total, 1)) * 100)}% do total`,
      bg: "bg-gradient-to-r from-green-500 to-emerald-500",
      icon: CheckCircle
    },
    {
      title: "Pendente Aprovação",
      value: stats.pending,
      change: "aguardando análise",
      bg: "bg-gradient-to-r from-orange-500 to-red-500",
      icon: AlertTriangle
    },
    {
      title: "Clientes Bloqueados",
      value: stats.blocked,
      change: "temporariamente",
      bg: "bg-gradient-to-r from-red-500 to-pink-500",
      icon: UserX
    }
  ];

  // Função para editar cliente
  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setFormData({
      email: client.email,
      password: "",
      nome_completo: client.nome_completo,
      telefone: client.telefone,
      cpf: client.cpf,
      profissao: client.profissao || "",
      cep: client.cep || "",
      logradouro: client.logradouro || "",
      numero: client.numero || "",
      complemento: client.complemento || "",
      bairro: client.bairro || "",
      cidade: client.cidade || "",
      estado: client.estado || "",
      is_active: client.is_active,
      approved: client.approved
    });
    setIsDialogOpen(true);
  };

  const filteredClients = clients.filter(client => {
    // ✅ NOVO: Filtrar clientes excluídos (que têm email com padrão "deleted_...@deleted.com")
    if (client.email.includes('@deleted.com') || client.email.startsWith('deleted_')) {
      
      return false;
    }
    
    // ✅ NOVO: Filtrar clientes inativos que foram "deletados" (opcional - pode remover se quiser ver inativos)
    // if (!client.is_active && client.email.includes('deleted')) {
    //   return false;
    // }
    
    // ✅ FIX: Filtro de busca original com proteção contra undefined
    return (client.nome_completo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           (client.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           (client.telefone || '').includes(searchTerm) ||
           (client.cpf || '').includes(searchTerm);
  }).sort((a, b) => (a.nome_completo || '').localeCompare(b.nome_completo || '', 'pt-BR'));

  // Card mobile personalizado para clientes
  const renderClientMobileCard = (client: Client, index: number) => (
    <Card key={index} className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg">{client.nome_completo}</h3>
        <div className="flex gap-2">
          <Badge variant={client.is_active ? "default" : "secondary"}>
            {client.is_active ? "Ativo" : "Inativo"}
          </Badge>
          <Badge variant={client.approved ? "default" : "destructive"}>
            {client.approved ? "Aprovado" : "Pendente"}
          </Badge>
        </div>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-500" />
          <span>{client.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-gray-500" />
          <span>{client.telefone}</span>
        </div>
        <div className="flex items-center gap-2">
          <IdCard className="w-4 h-4 text-gray-500" />
          <span>{client.cpf}</span>
        </div>
        {client.profissao && (
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-gray-500" />
            <span>{client.profissao}</span>
          </div>
        )}
      </div>
      
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => handleEditClient(client)} className="flex-1">
          <Edit className="w-4 h-4 mr-2" />
          Editar
        </Button>
        <Button size="sm" variant="outline" onClick={() => handleOpenResetPassword(client)}>
          <Key className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="destructive" onClick={() => handleDeleteClient(client.id)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      {isClientBlocked(client) && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Cliente Bloqueado</span>
          </div>
          <p className="text-xs text-red-600 mt-1">
            Até: {new Date(client.bloqueio_ate!).toLocaleDateString('pt-BR')}
          </p>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => handleUnblockClient(client.id)}
            className="mt-2 text-xs h-7"
          >
            Desbloquear
          </Button>
        </div>
      )}
    </Card>
  );

  const handleOpenDialog = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        email: client.email,
        password: "", // Não carregamos a senha existente
        nome_completo: client.nome_completo,
        telefone: client.telefone,
        cpf: client.cpf,
        profissao: client.profissao || "",
        cep: client.cep || "",
        logradouro: client.logradouro || "",
        numero: client.numero || "",
        complemento: client.complemento || "",
        bairro: client.bairro || "",
        cidade: client.cidade || "",
        estado: client.estado || "",
        is_active: client.is_active,
        approved: client.approved
      });
    } else {
      setEditingClient(null);
      setFormData({
        email: "",
        password: "",
        nome_completo: "",
        telefone: "",
        cpf: "",
        profissao: "",
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        estado: "",
        is_active: true,
        approved: false
      });
    }
    setIsDialogOpen(true);
  };

  const handleOpenResetPassword = (client: Client) => {
    setResetPasswordClient(client);
    setGeneratedPassword('');
    setIsResetPasswordDialogOpen(true);
  };

  const handleCloseResetPassword = () => {
    setIsResetPasswordDialogOpen(false);
    setResetPasswordClient(null);
    setGeneratedPassword('');
  };

  const handleGeneratePassword = () => {
    setGeneratedPassword(generateRandomPassword());
  };

  const handleSaveResetPassword = async () => {
    if (!resetPasswordClient) {
      return;
    }

    if (!generatedPassword) {
      toast.error('Gere uma senha antes de salvar');
      return;
    }

    try {
      setSavingResetPassword(true);
      await clientService.resetClientPassword(resetPasswordClient.id, generatedPassword);
      toast.success('Senha redefinida com sucesso!');
      handleCloseResetPassword();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao redefinir senha';
      toast.error('Erro ao redefinir senha: ' + message);
    } finally {
      setSavingResetPassword(false);
    }
  };

  const handleSaveClient = async () => {
    try {
      setSavingClient(true);
      console.log('🔧 Iniciando salvamento do cliente...', { editingClient: !!editingClient, formData });
      
      // Validações básicas
      if (!formData.email || !formData.nome_completo || !formData.telefone || !formData.cpf) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      
      if (editingClient) {
        // Editar cliente existente
        
        const updateData: UpdateClientData = {
          email: formData.email,
          nome_completo: formData.nome_completo,
          telefone: cleanNumbers(formData.telefone), // Remover formatação antes de enviar
          cpf: formData.cpf, // ✅ Manter CPF original - backend faz a limpeza e validação
          profissao: formData.profissao || undefined,
          cep: formData.cep ? cleanNumbers(formData.cep) : undefined, // Remover formatação antes de enviar
          logradouro: formData.logradouro || undefined,
          numero: formData.numero || undefined,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro || undefined,
          cidade: formData.cidade || undefined,
          estado: formData.estado || undefined,
          is_active: formData.is_active,
          approved: formData.approved
        };
        
        
        console.log('📡 Enviando dados para atualização...', { id: editingClient.id, updateData });
        console.log('📡 Dados do cliente sendo editado:', editingClient);
        console.log('📡 FormData atual:', formData);
        console.log('🔍 CPF ESPECÍFICO sendo enviado:', {
          cpf_original: formData.cpf,
          cpf_tipo: typeof formData.cpf,
          cpf_length: formData.cpf?.length,
          cpf_chars: formData.cpf?.split('').map(c => `'${c}'`).join(', ')
        });
        
        const result = await clientService.updateClient(editingClient.id, updateData);
        console.log('📡 Resposta da API:', result);
        console.log('✅ Cliente atualizado com sucesso no backend');
        toast.success('Cliente atualizado com sucesso!');
      } else {
        // Criar novo cliente
        if (!formData.password) {
          toast.error('Senha é obrigatória para novos clientes');
          return;
        }
        
        
        const createData: CreateClientData = {
          email: formData.email,
          password: formData.password,
          nome_completo: formData.nome_completo,
          telefone: cleanNumbers(formData.telefone), // Remover formatação antes de enviar
          cpf: formData.cpf, // ✅ Manter CPF original - backend faz a limpeza e validação
          profissao: formData.profissao || undefined,
          cep: formData.cep ? cleanNumbers(formData.cep) : undefined, // Remover formatação antes de enviar
          logradouro: formData.logradouro || undefined,
          numero: formData.numero || undefined,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro || undefined,
          cidade: formData.cidade || undefined,
          estado: formData.estado || undefined,
          is_active: formData.is_active,
          approved: formData.approved
        };
        
        
        const newClient = await clientService.createClient(createData);
        
        toast.success('Cliente criado com sucesso!');
      }
      
      console.log('🔄 Fechando modal e recarregando dados...');
      setIsDialogOpen(false);
      await loadClients();
      await loadStats();
      console.log('✅ Modal fechado e dados recarregados');
    } catch (error: unknown) {
      console.error('❌ Erro completo ao salvar cliente:', error);
      console.error('❌ Tipo do erro:', typeof error);
      console.error('❌ Error.name:', error instanceof Error ? error.name : 'N/A');
      console.error('❌ Error.message:', error instanceof Error ? error.message : 'N/A');
      console.error('❌ Error.stack:', error instanceof Error ? error.stack : 'N/A');
      
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error('Erro ao salvar cliente: ' + message);
    } finally {
      setSavingClient(false);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (confirm("Tem certeza que deseja excluir este cliente?")) {
      try {
        await clientService.deleteClient(clientId);
        toast.success('Cliente removido com sucesso!');
        loadClients();
        loadStats();
      } catch (error: unknown) {
        console.error('Erro ao remover cliente:', error);
        const message = error instanceof Error ? error.message : 'Erro desconhecido ao remover cliente';
        toast.error('Erro ao remover cliente: ' + message);
      }
    }
  };

  const toggleClientApproval = async (clientId: string) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        await clientService.approveClient(clientId, !client.approved);
        toast.success(`Cliente ${!client.approved ? 'aprovado' : 'reprovado'} com sucesso!`);
        loadClients();
        loadStats();
      }
    } catch (error: unknown) {
      console.error('Erro ao alterar aprovação:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao alterar aprovação';
      toast.error('Erro ao alterar aprovação: ' + message);
    }
  };

  const toggleClientStatus = async (clientId: string) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        await clientService.activateClient(clientId, !client.is_active);
        toast.success(`Cliente ${!client.is_active ? 'ativado' : 'desativado'} com sucesso!`);
        loadClients();
        loadStats();
      }
    } catch (error: unknown) {
      console.error('Erro ao alterar status:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao alterar status';
      toast.error('Erro ao alterar status: ' + message);
    }
  };

  const handleUnblockClient = async (clientId: string) => {
    try {
      await clientService.unblockClient(clientId);
      toast.success('Cliente desbloqueado com sucesso!');
      loadClients();
      loadStats();
    } catch (error: unknown) {
      console.error('Erro ao desbloquear cliente:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao desbloquear cliente';
      toast.error('Erro ao desbloquear cliente: ' + message);
    }
  };

  return (
    <ResponsiveAdminLayout
      title="Gestão de Clientes"
      subtitle="Gerencie clientes, aprovações e bloqueios"
    >
        {/* Search and Action Buttons */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <Input
            placeholder="Pesquisar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingClient ? "Editar Cliente" : "Novo Cliente"}
                  </DialogTitle>
                </DialogHeader>
                
                <Tabs defaultValue="personal" className="py-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="personal">Dados Pessoais</TabsTrigger>
                    <TabsTrigger value="address">Endereço</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="personal" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome_completo">Nome Completo</Label>
                        <Input
                          id="nome_completo"
                          value={formData.nome_completo}
                          onChange={(e) => setFormData({...formData, nome_completo: e.target.value})}
                          placeholder="Nome completo do cliente"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email">E-mail</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="email@exemplo.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="telefone">Telefone</Label>
                        <Input
                          id="telefone"
                          value={formData.telefone}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          placeholder="(11) 99999-9999"
                          maxLength={15}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="cpf">CPF</Label>
                        <Input
                          id="cpf"
                          value={formData.cpf}
                          onChange={(e) => handleCpfChange(e.target.value)}
                          placeholder="123.456.789-01"
                          maxLength={14}
                        />
                      </div>
                    </div>
                    
                    {!editingClient && (
                      <div className="space-y-2">
                        <Label htmlFor="password">Senha *</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          placeholder="Senha para o novo cliente"
                          required
                        />
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="profissao">Profissão</Label>
                      <Input
                        id="profissao"
                        value={formData.profissao}
                        onChange={(e) => setFormData({...formData, profissao: e.target.value})}
                        placeholder="Profissão do cliente"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="is_active"
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                        />
                        <Label htmlFor="is_active">Cliente Ativo</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="approved"
                          checked={formData.approved}
                          onCheckedChange={(checked) => setFormData({...formData, approved: checked})}
                        />
                        <Label htmlFor="approved">Aprovado</Label>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="address" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cep">CEP</Label>
                        <div className="relative">
                          <Input
                            id="cep"
                            value={formData.cep}
                            onChange={(e) => handleCepChange(e.target.value)}
                            placeholder="12345-678"
                            maxLength={9}
                            disabled={loadingCep}
                          />
                          {loadingCep && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="estado">Estado</Label>
                        <Input
                          id="estado"
                          value={formData.estado}
                          onChange={(e) => setFormData({...formData, estado: e.target.value})}
                          placeholder="SP"
                          maxLength={2}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="logradouro">Logradouro</Label>
                      <Input
                        id="logradouro"
                        value={formData.logradouro}
                        onChange={(e) => setFormData({...formData, logradouro: e.target.value})}
                        placeholder="Rua das Flores"
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="numero">Número</Label>
                        <Input
                          id="numero"
                          value={formData.numero}
                          onChange={(e) => setFormData({...formData, numero: e.target.value})}
                          placeholder="123"
                        />
                      </div>
                      
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="complemento">Complemento</Label>
                        <Input
                          id="complemento"
                          value={formData.complemento}
                          onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                          placeholder="Apto 45"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bairro">Bairro</Label>
                        <Input
                          id="bairro"
                          value={formData.bairro}
                          onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                          placeholder="Centro"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="cidade">Cidade</Label>
                        <Input
                          id="cidade"
                          value={formData.cidade}
                          onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                          placeholder="São Paulo"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
                
                <div className="flex justify-end gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setIsDialogOpen(false)}
                    disabled={savingClient}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSaveClient}
                    disabled={savingClient}
                  >
                    {savingClient ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      editingClient ? "Salvar Alterações" : "Criar Cliente"
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
        </div>
        <Dialog open={isResetPasswordDialogOpen} onOpenChange={(open) => !open && handleCloseResetPassword()}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Redefinir senha do cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-client-name">Cliente</Label>
                <Input
                  id="reset-client-name"
                  value={resetPasswordClient?.nome_completo || ''}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="generated-password">Nova senha</Label>
                <Input
                  id="generated-password"
                  value={generatedPassword}
                  readOnly
                  placeholder="Clique em Gerar"
                />
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={handleGeneratePassword}
                  disabled={savingResetPassword}
                >
                  Gerar nova senha
                </Button>
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCloseResetPassword}
                    disabled={savingResetPassword}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveResetPassword}
                    disabled={savingResetPassword || !generatedPassword}
                  >
                    {savingResetPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

                  {/* Clients Table */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-0">
              <ResponsiveTable
                data={filteredClients}
                columns={columns}
                renderMobileCard={renderClientMobileCard}
                loading={loading}
                emptyMessage="Nenhum cliente encontrado"
              />
            </CardContent>
          </Card>
        
    </ResponsiveAdminLayout>
  );
};

export default AdminClients; 
